import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { instance } from "api/apiBase";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { getErrorMessage } from "utils/error";
import { ContactFormsMenuTrigger } from "./ContactFormsMenuTrigger";
import { useEligibleContactFormSources } from "./useEligibleContactFormSources";
import { ContactFormsSubmenuVariant } from "./types";

interface ContactFormsSubmenuProps {
  propertyId?: number;
  onCopyLink: (url: string) => void;
  variant?: ContactFormsSubmenuVariant;
  disabled?: boolean;
}

interface CreateLinkResponse {
  url: string;
}

export const ContactFormsSubmenu = ({
  propertyId,
  onCopyLink,
  variant = "FULL",
  disabled
}: ContactFormsSubmenuProps) => {
  const { eligibleSources, isLoading } = useEligibleContactFormSources();

  const { mutate: generateLink, isPending } = useMutation({
    mutationKey: ["LEAD_SOURCE_CONTACT_FORM_LINK_CREATE"],
    mutationFn: async ({ leadSourceId }: { leadSourceId: number }) => {
      const body = propertyId ? { property_id: propertyId } : {};
      const { data } = await instance.post<CreateLinkResponse>(
        `/portal/v1/boom_crm/lead_sources/${leadSourceId}/contact_form_links`,
        body
      );
      return data;
    },
    onSuccess: (data) => {
      onCopyLink(data.url);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    }
  });

  const headerAction = {
    title: <p className="text-sm font-medium text-content-400">Lead source</p>,
    className: "!cursor-default !bg-white hover:!bg-white !py-2",
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
    }
  };

  const sourceActions = eligibleSources.map((source) => ({
    title: (
      <div className="flex items-center gap-3 h-8">
        <span
          className="inline-block size-4 rounded-full shrink-0"
          style={{ backgroundColor: source.color ?? undefined }}
        />
        <h2 className="text-base font-medium text-content-1000 truncate">
          {source.name}
        </h2>
      </div>
    ),
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      generateLink({ leadSourceId: source.id });
    }
  }));

  return (
    <ContextMenuPopover
      placement={variant === "BUTTON" ? "bottom-end" : "left-start"}
      disabled={disabled || isLoading || isPending}
      contentClassName={
        variant === "BUTTON"
          ? "[&>div]:!min-w-[182px] [&>div]:!w-[182px]"
          : "[&>div]:!-translate-x-4 [&>div]:!min-w-[182px] [&>div]:!w-[182px]"
      }
      cbTrigger={() => (
        <ContactFormsMenuTrigger variant={variant} disabled={disabled} />
      )}
      actions={[headerAction, ...sourceActions]}
    />
  );
};
